import { useNavigate } from "react-router-dom";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useTranslation } from "react-i18next";

import { listSelector } from "@/pages/Private/redux/list/list.slice";
import { removeLeadWithFade } from "@/pages/Private/redux/list/removeLeadWithFade";
import { useSendMessagesMutation } from "@/pages/Private/redux/schedule/schedule.api";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { classNames } from "@/utils/classNames";

import { GenerateBar } from "@/pages/Private/pages/Messages/components/GenerateBar/GenerateBar";
import { useIsFeatureActive } from "@/utils/useIsFeatureActive";
import { FeaturesCodes } from "@/pages/Private/pages/Users/enum/codes";

import { useSnackBar } from "@/utils/useSnackbar";
import { useSingleFlight } from "@/utils/useSingleFlight";

import { authSelector } from "@/pages/Public/redux/auth.slice";

import {
	AiDraftItem,
	CopywritingChannel,
} from "@/pages/Private/pages/Messages/components/GenerateBar/types";
import { DraftReplies } from "@/enum/draft-replies.enum";
import { sequenceToDraftItems } from "@/pages/Private/pages/Messages/components/GenerateBar/sequenceToDraftItems.util";

import { Draft, DraftTiming, createDefaultDraft, timingFromDays } from "./inboxDraft.types";
import { isInvitationPendingRefusal, sendFailureMessageKey } from "./sendFailureMessage.util";
import { resolveSenderChannelOption, SenderChannelPick } from "./resolveSenderChannelOption.util";
import { ChatlogFrame } from "../components/ChatlogFrame";
import { DraftCard } from "../components/DraftCard";
import { DraftEditorPanel } from "../components/DraftEditorPanel";
import { useDraftSignature } from "./useDraftSignature";
import { useInsertVariableAtCaret } from "./useInsertVariableAtCaret";
import { UndoSendToast } from "../components/UndoSendToast";
import { ChatSendBar } from "../components/ChatSendBar";
import { FloatingGeneratePanel } from "../components/FloatingGeneratePanel";
import { GenerateBarCollapsedStrip } from "../components/GenerateBarCollapsedStrip";
import { ThreadConversationList } from "../components/ThreadConversationList";
import { SendTestChatMessageModal } from "../components/SendTestChatMessageModal";
import { SenderRecipientOption } from "../components/SenderRecipientDropdown";
import { hasDraftChannelMismatch, isSendableDraft } from "./draftValidation.util";
import { useDraftSendBlockers } from "./useDraftSendBlockers";
import { useEditorAutoCollapse } from "./useEditorAutoCollapse";
import { buildRecipientOptions } from "./buildRecipientOptions.util";
import { useUndoableSend } from "./useUndoableSend";
import { removeDraftAt } from "./removeDraftAt.util";
import { applyDraftFromUpdate, applyDraftToUpdate } from "./updateDraftSide.util";
import { refreshDraftSenderLabels } from "./refreshDraftSenderLabels.util";
import { buildFilteredAiDrafts } from "./buildFilteredAiDrafts.util";
import { buildComposerSendRequest, composerSentMessage } from "./composerSendRequest.util";
import { useDraftDefaults } from "./useDraftDefaults";
import { useActiveDraftFields } from "./useActiveDraftFields";
import { useSenderConfirmGate } from "./useSenderConfirmGate";
import { ReplyDraftEntry } from "./buildReplyPrefillDrafts.util";
import { buildInitialInboxDrafts } from "./buildInitialInboxDrafts.util";
import { copywritingFailureOf, generateBarLeadProps, keepsAiDrafts } from "./generateBarLead.util";
import { applyAutoInviteFlagToFirstDraft } from "./resolveDraftInviteNoteFlag.util";
import { applyDefaultsToPlaceholderDrafts } from "./applyDefaultsToPlaceholderDrafts.util";
import { applyAiSenderToPlaceholderDrafts } from "./applyAiSenderToPlaceholderDrafts.util";
import { buildReinjectDrafts, resolveReinjectTarget } from "./buildReinjectDrafts.util";
import { resolveGenerateEntryVisibility } from "./shouldShowInlineGenerateBar.util";
import { useMessageTemplates } from "./useMessageTemplates";
import { useGeneratePanelState } from "./useGeneratePanelState";
import { appendTemplateToMessage } from "./appendTemplateToMessage.util";
import { resolveAiSenderUserId, type AiSender } from "./resolveAiSenderUserId.util";
import { useLeadSendGate } from "./useLeadSendGate";
import { rebuildDraftsForChannelSwitch } from "./rebuildDraftsForChannelSwitch.util";
import { useComposerLinkedinSenders } from "./useComposerLinkedinSenders";
import { resolvePendingInvitationView } from "./pendingInvitation.util";
import { useSaveCopywritingDraft } from "./useSaveCopywritingDraft";
import { getSuggestions } from "../../../utils/column-utils";
import { getSuggestionLabel } from "./suggestions.utils";
import { resolveSenderContext } from "./resolveSenderContext.util";
import { scrollChatToTarget } from "./scrollChatToTarget.util";
import { UseAddInboxChatOptions } from "./useAddInboxChat.types";

export interface FollowUp {
	message: string;
	days: number;
}

export const useAddInboxChat = (options?: UseAddInboxChatOptions) => {
	const {
		prefillDrafts = false,
		lead: leadOverride,
		portalContainer,
		scopeGeneratePanelToPortal = false,
		panelLayout = false,
		collapsedGenerateBar = false,
		hideGenerateBar = false,
		onSent,
		undoableSend = false,
	} = options ?? {};
	const { t } = useTranslation();
	const dispatch = useAppDispatch();
	const { inbox: sliceInbox, currentLeadIndex: sliceLeadIndex } = useAppSelector(listSelector);
	// Virtual inbox: an explicit lead becomes a single-entry inbox so the existing reads resolve to it.
	const inbox = useMemo(
		() => (leadOverride ? [leadOverride] : sliceInbox),
		[leadOverride, sliceInbox]
	);
	const currentLeadIndex = leadOverride ? 0 : sliceLeadIndex;
	const { user } = useAppSelector(authSelector);
	const isCopywritingActive = useIsFeatureActive(FeaturesCodes.COPYWRITING_SETTINGS);
	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();
	const navigate = useNavigate();
	const lastReceivedRef = useRef<HTMLDivElement>(null);
	// The chat log's own overflow-y-auto scroll container.
	const chatlogScrollRef = useRef<HTMLDivElement>(null);

	// --- Draft state ---
	const [drafts, setDrafts] = useState<Draft[]>([createDefaultDraft(1)]);
	const [activeDraftIndex, setActiveDraftIndex] = useState<number>(0);
	const [collapsedDrafts, setCollapsedDrafts] = useState<Record<number, boolean>>({});
	// Tracks which lead the current `drafts` were built for. Used to avoid showing
	// lead-specific affordances (e.g. Save draft) during the transient render after
	// switching prospects, before the prefill effect rebuilds the drafts.
	const [draftsLeadId, setDraftsLeadId] = useState<number | undefined>(undefined);
	// eslint-disable-next-line @eslint-react/naming-convention/ref-name
	const draftIdCounter = useRef<number>(2);

	// --- Store full AI draft items for re-injection on sender channel switch ---
	const aiDraftItemsRef = useRef<AiDraftItem[]>([]);
	const aiSenderRef = useRef<AiSender>({});
	const prevSenderChannelRef = useRef<"email" | "linkedin" | undefined>(undefined);

	// --- Persist edited AI copywriting drafts back onto the lead ---
	const {
		saveDraft: saveCopywritingDraft,
		isSaving: isSavingCopywritingDraft,
		canSave: canSaveCopywritingDraft,
	} = useSaveCopywritingDraft({
		lead: inbox[currentLeadIndex],
		drafts,
		draftsLeadId,
		onSnackbar: handleOpenSnackbar,
	});
	// Set to true when the user manually changes the channel of a draft so the
	// auto-resolve effect stops following conversation history for this session.
	const isManualChannelSwitchRef = useRef<boolean>(false);

	// --- Sender/Recipient data: the senders, their LinkedIn state per lead (PREC-4216), the options ---
	const {
		sendingUsers,
		connectedUserIds,
		senderStates,
		pendingInvitationUserIds,
		connectionCheckDone,
		recheck: recheckSenderStates,
		lastSenderUserId,
		isLastSenderConnected,
		senderOptions,
		waitsForAcceptance,
	} = useComposerLinkedinSenders(inbox[currentLeadIndex], user?.id);

	const [isEditorCollapsed, setIsEditorCollapsed] = useState<boolean>(false);

	// --- Send test message modal (tests the active draft) ---
	const [showSendTestModal, setShowSendTestModal] = useState<boolean>(false);

	const [sendMessages, { isLoading: isSendingMessage }] = useSendMessagesMutation();

	// --- Undo send (deferred send + toast) ---
	// Commit finalizes the send in the UI: the sent card fades out, then its
	// removal re-points `currentLeadIndex` at the next reply (auto-advance) and
	// the lead-change effect below rebuilds the drafts for it.
	const undoable = useUndoableSend({
		onCommit: (listPersonId) => removeLeadWithFade(dispatch, listPersonId, onSent),
		onUndone: () => handleOpenSnackbar(t("inbox.undoSend.undone")),
		onUndoFailed: () => handleOpenSnackbar(t("inbox.undoSend.undoFailed")),
	});

	// Flags scrolls we trigger ourselves so the auto-collapse listener can tell
	// them apart from the user reading the thread (see useEditorAutoCollapse).
	const programmaticScrollRef = useRef<boolean>(false);
	const markProgrammaticScroll = useCallback(() => {
		programmaticScrollRef.current = true;
		setTimeout(() => {
			programmaticScrollRef.current = false;
		}, 800);
	}, []);

	// Scroll the chat log container directly — scrollIntoView would also scroll
	// the overflow-hidden Messages content wrapper, leaving the pane stuck with
	// its top controls clipped under the tab bar (see scrollChatToTarget).
	const scrollToBottom = useCallback(() => {
		markProgrammaticScroll();
		setTimeout(() => {
			scrollChatToTarget(chatlogScrollRef.current, lastReceivedRef.current, "smooth");
		}, 100);
	}, [markProgrammaticScroll]);

	useEffect(() => {
		if (!inbox[currentLeadIndex]) {
			return;
		}

		markProgrammaticScroll();
		setTimeout(() => {
			scrollChatToTarget(chatlogScrollRef.current, lastReceivedRef.current, "auto");
		}, 100);
	}, [inbox[currentLeadIndex]?.id]);

	// Collapse the editor to its strip while the user reads the thread: a real
	// scroll or a click in the chat log (outside the draft cards) collapses it.
	const collapseEditor = useCallback(() => setIsEditorCollapsed(true), []);

	useEditorAutoCollapse({
		scrollRef: chatlogScrollRef,
		programmaticScrollRef,
		enabled: !isEditorCollapsed,
		onCollapse: collapseEditor,
	});
	// --- Derived message state (backward compat) ---
	const message = drafts[activeDraftIndex]?.content ?? "";
	const setMessage = useCallback(
		(val: string) => {
			setDrafts((prev) =>
				prev.map((d, i) => (i === activeDraftIndex ? { ...d, content: val } : d))
			);
		},
		[activeDraftIndex]
	);

	// --- Template variables ---
	// Ref to the composer textarea, used to insert tokens at the caret.
	const composerRef = useRef<HTMLTextAreaElement | null>(null);

	// Available variable keys for the active lead, including sender-only vars
	// ({signature}, {senderFirstName}, {senderLastName}, {unsubscribe_link}).
	const suggestions = useMemo(
		() => getSuggestions(inbox[currentLeadIndex], true),
		[inbox, currentLeadIndex]
	);

	const variables = useMemo(
		() =>
			suggestions.map((key) => ({
				key,
				label: getSuggestionLabel(key, inbox[currentLeadIndex]) || key,
			})),
		[suggestions, inbox, currentLeadIndex]
	);

	const insertVariableIntoActiveDraft = useInsertVariableAtCaret(
		composerRef,
		drafts[activeDraftIndex]?.content ?? "",
		setMessage
	);
	const draftSignature = useDraftSignature(setDrafts, activeDraftIndex);

	// --- Message templates (insert + save-as) ---
	// Templates are appended to the active draft's existing content rather than
	// replacing it, so picking a template never discards what was already typed.
	const handleInsertTemplate = useCallback(
		(content: string) => {
			setMessage(appendTemplateToMessage(drafts[activeDraftIndex]?.content ?? "", content));
		},
		[drafts, activeDraftIndex, setMessage]
	);

	const {
		showTemplateModal,
		showCreateTemplateModal,
		dropdownPosition,
		setDropdownPosition,
		handleOpenTemplateModal,
		handleSaveAsTemplate,
		renderTemplateButton,
		renderMessageTemplatesModal,
		renderCreateTemplateModal,
	} = useMessageTemplates({ onInsertTemplate: handleInsertTemplate, portalContainer });

	const recipientOptions: SenderRecipientOption[] = useMemo(
		() => buildRecipientOptions(inbox[currentLeadIndex]),
		[inbox, currentLeadIndex]
	);

	// The default sender is me or nothing, never a colleague (PREC-4312; see resolveDefaultDraftDefaults).
	const { draftDefaults, fallbackNotice, me } = useDraftDefaults({
		lead: inbox[currentLeadIndex],
		senderOptions,
		recipientOptions,
		sendingUsers: sendingUsers?.data,
		user,
		connectionCheckDone,
		connectedUserIds,
		activeFromUserId: drafts[activeDraftIndex]?.from?.userId,
	});

	// --- Reset drafts when the lead changes, or its AI output arrives on the Chat tab (PREC-3710) ---
	useEffect(() => {
		// A send still inside its undo window is finalized when the user moves on
		// to another lead (no-op when nothing is pending).
		undoable.commit();

		const lead = inbox[currentLeadIndex];
		const { from: defaultFrom, to: defaultTo } = draftDefaults;
		const draftReplies = user?.company?.draftReplies;
		const maxDrafts =
			draftReplies === DraftReplies.THREE ? 3 : draftReplies === DraftReplies.ONE ? 1 : 0;

		setIsEditorCollapsed(false);
		isManualChannelSwitchRef.current = false;
		// The drafts rebuilt below belong to this lead — record it so lead-specific
		// affordances don't flash on the stale drafts during a prospect switch.
		// eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
		setDraftsLeadId(lead?.id);

		// Always load AI items into the ref when copywriting output exists, even if
		// we don't render them as drafts. This enables channel switching later.
		// Pass contextPayload/messagingStrategy so each item carries its `aiOrigin`
		// (tone-of-voice metadata) — this is what lets us map edits back to the
		// sequence on save and capture tone of voice on send.
		const aiItemsForRef =
			lead?.copywritingOutput?.type === "full_sequence"
				? sequenceToDraftItems(
						lead.copywritingOutput,
						CopywritingChannel.BOTH,
						lead.copywritingOutput.contextPayload,
						lead.copywritingOutput.messagingStrategy
					)
				: [];
		const aiSenderForRef = lead?.copywritingOutput
			? resolveAiSenderUserId(lead.copywritingOutput?.contextPayload)
			: {};

		aiDraftItemsRef.current = aiItemsForRef;
		aiSenderRef.current = aiSenderForRef;

		// Prefer persisted AI copywriting output over reply1/2/3 when available.
		const hasAiOutput =
			prefillDrafts &&
			keepsAiDrafts(lead?.copywritingStatus) &&
			lead?.copywritingOutput?.type === "full_sequence";

		if (hasAiOutput && aiItemsForRef.length > 0) {
			const result = buildFilteredAiDrafts({
				items: aiItemsForRef,
				listPersonId: lead?.id,
				prospectEmail: lead?.prospect?.email,
				prospectHasLinkedinUrl: !!lead?.prospect?.linkedinUrl,
				conversations: lead?.prospect?.conversations ?? [],
				senderOptions,
				recipientOptions,
				user: user ?? undefined,
				sendingUsers,
				connectionCheckDone,
				connectedUserIds,
				senderStates,
				startingDraftId: 1,
				aiSender: aiSenderForRef,
			});

			if (result.drafts.length > 0) {
				prevSenderChannelRef.current = result.activeChannel;
				// eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
				setDrafts(result.drafts);
				draftIdCounter.current = result.nextDraftId;
				// eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
				setActiveDraftIndex(0);
				// eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
				setCollapsedDrafts({});

				return;
			}
		}

		// Build drafts from reply1/reply2/reply3 when they exist on the listPerson (each carries an
		// aiOrigin snapshot so an edited-then-sent reply feeds the tone-of-voice learning loop),
		// otherwise a single empty reply draft. Both inherit the incoming email's "Re: …" subject.
		const replyEntries: ReplyDraftEntry[] = [
			{ content: lead?.reply1, days: lead?.replyDate1, reasoning: lead?.replyReasoning1 },
			{ content: lead?.reply2, days: lead?.replyDate2, reasoning: lead?.replyReasoning2 },
			{ content: lead?.reply3, days: lead?.replyDate3, reasoning: lead?.replyReasoning3 },
		];

		const initial = buildInitialInboxDrafts({
			replyEntries,
			prefillDrafts,
			maxDrafts,
			conversations: lead?.prospect?.conversations ?? [],
			defaultFrom,
			defaultTo,
		});

		// eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
		setDrafts(initial.drafts);
		draftIdCounter.current = initial.nextDraftId;

		// eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
		setActiveDraftIndex(0);
		// eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
		setCollapsedDrafts({});
	}, [currentLeadIndex, inbox[currentLeadIndex]?.id, leadOverride?.copywritingGeneratedAt]);

	// --- Re-resolve sender/recipient when senderOptions change (e.g. after LinkedIn connection check completes) ---
	// Only fills in drafts whose `from` is still a placeholder; drafts injected
	// by the AI flow or manually edited by the user keep their existing sender.
	useEffect(() => {
		if (!connectionCheckDone) {
			return;
		}

		// AI drafts built before the LinkedIn connection check completed fell back to
		// the "Sender" placeholder (no LinkedIn option existed yet). Now that the
		// options are ready, resolve the AI-chosen sender for those placeholders
		// instead of applying the generic default (PREC-3674).
		if (aiDraftItemsRef.current.length > 0) {
			setDrafts((prev) =>
				applyAiSenderToPlaceholderDrafts({
					drafts: prev,
					senderOptions,
					aiSender: aiSenderRef.current,
					threadSender: draftDefaults.threadSender,
					user,
				})
			);

			return;
		}

		const { from: defaultFrom, to: defaultTo } = draftDefaults;

		setDrafts((prev) => applyDefaultsToPlaceholderDrafts({ drafts: prev, defaultFrom, defaultTo }));
	}, [connectionCheckDone, senderOptions, user, draftDefaults]);

	// --- Auto-flag the first draft as a LinkedIn invite note (connection request)
	// when its sender is unconnected to the prospect. Only the first draft is
	// eligible; follow-ups are never auto-flagged. ---
	const firstDraftFromUserId = drafts[0]?.from?.userId;
	const firstDraftFromType = drafts[0]?.from?.type;
	// A rebuild can set the flag without changing the sender: re-decide then too (PREC-4216).
	const firstDraftIsInvite = !!drafts[0]?.isInviteNote;

	useEffect(() => {
		if (!connectionCheckDone) {
			return;
		}

		setDrafts((prev) =>
			applyAutoInviteFlagToFirstDraft(prev, connectionCheckDone, connectedUserIds, senderStates)
		);
	}, [
		connectionCheckDone,
		connectedUserIds,
		senderStates,
		firstDraftFromUserId,
		firstDraftFromType,
		firstDraftIsInvite,
	]);

	// --- Keep draft sender labels in sync with the latest sender options. Once the
	// LinkedIn connection check resolves, the canonical options drop the stale
	// "… — will send invite" suffix; refresh the selected senders' labels to match
	// (matching by identity preserves AI- and user-chosen senders). ---
	useEffect(() => {
		if (!connectionCheckDone) {
			return;
		}

		setDrafts((prev) => refreshDraftSenderLabels(prev, senderOptions));
	}, [connectionCheckDone, senderOptions]);

	// --- Draft management handlers ---
	const handleAddFollowUp = useCallback(() => {
		const newId = draftIdCounter.current++;
		const lastDraft = drafts[drafts.length - 1];
		const prevDays = lastDraft?.timing?.scheduledInDays ?? 0;

		setDrafts((prev) => [
			...prev,
			createDefaultDraft(
				newId,
				lastDraft?.from ? { ...lastDraft.from } : undefined,
				lastDraft?.to ? { ...lastDraft.to } : undefined,
				undefined,
				timingFromDays(prevDays + 1)
			),
		]);
		setActiveDraftIndex(drafts.length);
		// Scroll to bottom after the new draft card renders
		setTimeout(() => scrollToBottom(), 100);
	}, [drafts, scrollToBottom]);

	const handleDeleteDraft = useCallback(
		(index: number) => {
			if (drafts.length <= 1) {
				return;
			}

			setDrafts((prev) => removeDraftAt(prev, index));
			setActiveDraftIndex((prev) => (prev >= index && prev > 0 ? prev - 1 : prev));
		},
		[drafts.length]
	);

	const handleDeleteAllDrafts = useCallback(() => {
		if (drafts.length <= 1) {
			return;
		}

		const { from: defaultFrom, to: defaultTo } = draftDefaults;

		setDrafts([createDefaultDraft(draftIdCounter.current++, defaultFrom, defaultTo)]);
		setActiveDraftIndex(0);
		setCollapsedDrafts({});
		setIsEditorCollapsed(true);
	}, [drafts.length, draftDefaults]);

	/**
	 * Inject AI-generated drafts into the draft system.
	 * Each item should have { content, subject?, channel, scheduledInDays?, isInviteNote? }.
	 *
	 * When any item has isInviteNote=true, we eagerly compute sender options with
	 * unconnected LinkedIn senders included so the LinkedIn drafts get assigned
	 * a valid sender immediately (before the next render cycle).
	 */
	const injectAiDrafts = useCallback(
		(items: AiDraftItem[], contextPayload?: unknown) => {
			// Store the full unfiltered items for re-injection on sender channel switch
			aiDraftItemsRef.current = items;
			const aiSender = resolveAiSenderUserId(contextPayload);

			aiSenderRef.current = aiSender;

			const lead = inbox[currentLeadIndex];

			const result = buildFilteredAiDrafts({
				items,
				listPersonId: lead?.id,
				prospectEmail: lead?.prospect?.email,
				prospectHasLinkedinUrl: !!lead?.prospect?.linkedinUrl,
				conversations: lead?.prospect?.conversations ?? [],
				senderOptions,
				recipientOptions,
				user: user ?? undefined,
				sendingUsers,
				connectionCheckDone,
				connectedUserIds,
				senderStates,
				startingDraftId: draftIdCounter.current,
				aiSender,
			});

			// Track the initial channel so the re-inject effect doesn't fire immediately
			prevSenderChannelRef.current = result.activeChannel;

			if (result.drafts.length > 0) {
				draftIdCounter.current = result.nextDraftId;
				setDrafts(result.drafts);
				setActiveDraftIndex(0);
				setCollapsedDrafts({});
				setIsEditorCollapsed(false);
				setTimeout(() => scrollToBottom(), 100);
			}
		},
		[
			inbox,
			currentLeadIndex,
			senderOptions,
			recipientOptions,
			user,
			scrollToBottom,
			sendingUsers,
			connectionCheckDone,
			connectedUserIds,
			senderStates,
		]
	);

	// --- Re-inject AI drafts when sender channel changes ---
	useEffect(() => {
		const lead = inbox[currentLeadIndex];

		if (!lead || aiDraftItemsRef.current.length === 0) {
			return;
		}

		// Stop auto-following conversation history once the user manually switched
		// the channel of a draft so their choice sticks.
		if (isManualChannelSwitchRef.current) {
			return;
		}

		// The channel the history points to now, with its sender and recipient: null when it has not
		// changed or is not set yet.
		const target = resolveReinjectTarget({
			conversations: lead.prospect?.conversations ?? [],
			listPersonId: lead.id,
			senderOptions,
			recipientOptions,
			aiItems: aiDraftItemsRef.current,
			previousChannel: prevSenderChannelRef.current,
		});

		if (!target) {
			return;
		}

		prevSenderChannelRef.current = target.channel;

		if (target.items.length === 0) {
			return;
		}

		const { drafts: newDrafts, nextDraftId } = buildReinjectDrafts({
			items: target.items,
			senderOptions,
			aiSender: aiSenderRef.current,
			fallbackSender: target.sender,
			recipientOption: target.recipient,
			user,
			startingDraftId: draftIdCounter.current,
			pendingInvitations: senderStates.pendingInvitations,
		});

		draftIdCounter.current = nextDraftId;
		setDrafts(newDrafts);
		setActiveDraftIndex(0);
		setCollapsedDrafts({});
		setIsEditorCollapsed(false);
		setTimeout(() => scrollToBottom(), 100);
	}, [senderOptions, recipientOptions]);

	const handleToggleCollapse = useCallback((draftId: number) => {
		setCollapsedDrafts((prev) => ({ ...prev, [draftId]: !prev[draftId] }));
	}, []);

	const handleUpdateDraftTiming = useCallback((index: number, timing: DraftTiming) => {
		setDrafts((prev) => prev.map((d, i) => (i === index ? { ...d, timing } : d)));
	}, []);

	// A manual channel switch rebuilds the drafts from the stored AI items for the new channel;
	// false when there are none, so the caller falls back to a plain single-side update.
	const rebuildOnChannelSwitch = useCallback(
		(pinnedSide: "from" | "to", option: SenderRecipientOption): boolean => {
			const rebuilt =
				aiDraftItemsRef.current.length > 0 &&
				rebuildDraftsForChannelSwitch({
					aiItems: aiDraftItemsRef.current,
					targetChannel: option.type,
					pinnedSide,
					pinnedOption: option,
					senderOptions,
					recipientOptions,
					user: me,
					startingDraftId: draftIdCounter.current,
					pendingInvitations: senderStates.pendingInvitations,
				});

			if (!rebuilt) {
				return false;
			}

			draftIdCounter.current = rebuilt.nextDraftId;
			prevSenderChannelRef.current = option.type;
			setDrafts(rebuilt.drafts);
			setActiveDraftIndex(0);
			setCollapsedDrafts({});
			setTimeout(() => scrollToBottom(), 100);

			return true;
		},
		[senderOptions, recipientOptions, me, scrollToBottom, senderStates]
	);

	const handleUpdateDraftFrom = useCallback(
		(index: number, option: SenderRecipientOption) => {
			// Check if this is a channel switch
			const currentDraftChannel = drafts[index]?.from?.type;
			const isChannelSwitch = !!currentDraftChannel && currentDraftChannel !== option.type;

			if (isChannelSwitch) {
				isManualChannelSwitchRef.current = true;
			}

			if (isChannelSwitch && rebuildOnChannelSwitch("from", option)) {
				return;
			}

			// Standard sender update (no AI re-injection) — applies to every draft
			setDrafts((prev) => applyDraftFromUpdate(prev, option, recipientOptions));
		},
		[recipientOptions, drafts, rebuildOnChannelSwitch]
	);

	const handleUpdateDraftTo = useCallback(
		(index: number, option: SenderRecipientOption) => {
			// Check if this is a channel switch on the recipient side
			const currentDraftChannel = drafts[index]?.to?.type;
			const isChannelSwitch = !!currentDraftChannel && currentDraftChannel !== option.type;

			if (isChannelSwitch) {
				isManualChannelSwitchRef.current = true;
			}

			if (isChannelSwitch && rebuildOnChannelSwitch("to", option)) {
				return;
			}

			// Standard recipient update (see applyDraftToUpdate) — applies to every draft
			setDrafts((prev) => applyDraftToUpdate(prev, option, senderOptions, me));
		},
		[senderOptions, drafts, me, rebuildOnChannelSwitch]
	);

	const { handleUpdateDraftCc, handleUpdateDraftBcc, handleToggleCcBcc, handleUpdateDraftSubject } =
		useActiveDraftFields(setDrafts, activeDraftIndex);

	// --- GenerateBar → editor sender/channel sync ---
	// When the user changes sender/channel in the GenerateBar panel, mirror that
	// pick into the active draft by finding the matching SenderRecipientOption
	// and delegating to the existing single-draft update path.
	// The drafts on screen belong to the current lead only once its reset ran: until then they are
	// the previous lead's, and neither seed the GenerateBar nor take its pick (PREC-4312).
	const draftsAreCurrent = draftsLeadId === inbox[currentLeadIndex]?.id;
	const seedFrom = draftsAreCurrent ? drafts[activeDraftIndex]?.from : undefined;
	// The active draft waits for its sender's pending invitation (PREC-4216, mockup A2), as this
	// lead's own check answered it.
	const pendingInvitation =
		draftsAreCurrent && connectionCheckDone
			? resolvePendingInvitationView(drafts[activeDraftIndex], senderStates, senderOptions)
			: null;

	const handleSenderChannelChange = useCallback(
		(pick: SenderChannelPick) => {
			if (drafts.length === 0 || !draftsAreCurrent) {
				return;
			}

			const match = resolveSenderChannelOption({
				pick,
				senderOptions,
				currentFrom: drafts[activeDraftIndex]?.from,
			});

			if (match) {
				handleUpdateDraftFrom(activeDraftIndex, match);
			}
		},
		[senderOptions, drafts, activeDraftIndex, handleUpdateDraftFrom, draftsAreCurrent]
	);

	// --- Channel mismatch, missing subject, invite note overflow, no real sender (useDraftSendBlockers) ---
	const { isBlocked } = useDraftSendBlockers(drafts, isSendableDraft, senderOptions);

	// A blacklisted lead is written to only on purpose (PREC-2548): Send asks first.
	const { clearToSend, overrideModal } = useLeadSendGate(inbox[currentLeadIndex]);
	// Sending as someone else is confirmed first, once per batch (PREC-4312): Cancel sends nothing.
	const { confirmSenders, senderConfirmModal } = useSenderConfirmGate();

	// --- Send message handler (unified batch) ---
	const sendDrafts = useCallback(async () => {
		if (isBlocked) {
			return;
		}

		const sendableDrafts = drafts.filter(isSendableDraft);

		// A cancel, a sender the user did not confirm, or a lead that may not be taken off the
		// blacklist, sends nothing; the drafts stay. Asked before the undo window starts.
		const senders = sendableDrafts.map((draft) => draft.from);

		if (!sendableDrafts.length || !(await confirmSenders(senders)) || !(await clearToSend())) {
			return;
		}

		// "Send when accepted" (PREC-4216): a sender whose invitation is pending parks the message.
		const { request, acceptanceDelayDays } = buildComposerSendRequest({
			lead: inbox[currentLeadIndex],
			sendableDrafts,
			signatureDefault: draftSignature.signatureDefault,
			pendingInvitations: senderStates.pendingInvitations,
		});

		// PREC-3900: both send paths reject with a 403 `PLAN_INACTIVE` once the company has
		// cancelled, and neither used to be caught — `handleSendMessage` is invoked as a floating
		// promise, so a cancelled customer clicked Send and got total silence plus an unhandled
		// rejection. `rejectRedirect` deliberately does not navigate on it either, so the toast
		// here is the only feedback there is.
		try {
			if (undoableSend) {
				// Deferred send: the API holds the messages for the undo window. Drafts
				// and the inbox stay untouched until the toast expires (see useUndoableSend);
				// the commit callback then removes the lead, which rebuilds the drafts.
				await undoable.send(request, acceptanceDelayDays);

				return;
			}

			await sendMessages(request).unwrap();
		} catch (error) {
			handleOpenSnackbar(t(sendFailureMessageKey(error)), 8000);

			// The invitation turned pending after the composer looked: ask again, so the notice and
			// "Send when accepted" appear for the next click (PREC-4216).
			if (isInvitationPendingRefusal(error)) {
				recheckSenderStates();
			}

			return;
		}

		handleOpenSnackbar(composerSentMessage(sendableDrafts.length, acceptanceDelayDays, t), 5000);

		// Reset drafts onto the default sender: a blank sender would block the next Send.
		setDrafts([createDefaultDraft(draftIdCounter.current++, draftDefaults.from, draftDefaults.to)]);
		setActiveDraftIndex(0);
		setCollapsedDrafts({});
		onSent?.();
	}, [
		drafts,
		inbox,
		currentLeadIndex,
		sendMessages,
		handleOpenSnackbar,
		isBlocked,
		onSent,
		undoableSend,
		draftSignature.signatureDefault,
		draftDefaults,
		undoable,
		confirmSenders,
		clearToSend,
		t,
		senderStates,
		recheckSenderStates,
	]);

	// A write runs once (§24c): a double click, or Cmd+Enter during the click, sends one request.
	const { run: runSendOnce, isRunning: isSendRunning } = useSingleFlight();
	const handleSendMessage = useCallback(() => runSendOnce(sendDrafts), [runSendOnce, sendDrafts]);

	// --- Generate panel state + handlers ---
	const {
		showGeneratePanel,
		generateTriggerDescriptionOverride,
		openGeneratePanel,
		handleRequestCloseGeneratePanel,
		handleGenerationComplete,
	} = useGeneratePanelState<Parameters<typeof injectAiDrafts>[0]>(injectAiDrafts);

	// Shared GenerateBar wiring for the inline (chat log) and floating (portal) "Tell AI" surfaces.
	const renderGenerateBar = (opts: {
		hasConversations: boolean;
		onGenerationComplete: (drafts: AiDraftItem[], contextPayload?: unknown) => void;
		triggerDescription?: string | null;
	}) => (
		<GenerateBar
			{...generateBarLeadProps(inbox[currentLeadIndex])}
			connectedUserIds={connectedUserIds}
			connectionCheckDone={connectionCheckDone}
			hasConversations={opts.hasConversations}
			pendingInvitationUserIds={pendingInvitationUserIds}
			initialChannel={seedFrom?.type}
			initialSenderUserId={seedFrom?.userId}
			isLastSenderConnected={isLastSenderConnected}
			lastSenderUserId={lastSenderUserId}
			onGenerationComplete={opts.onGenerationComplete}
			compact={panelLayout}
			onSenderChannelChange={handleSenderChannelChange}
			portalContainer={portalContainer}
			triggerDescription={opts.triggerDescription}
			upcomingLeadIds={inbox.slice(currentLeadIndex).map((item) => item.id)}
		/>
	);

	const renderChatlog = (selected: number[], isLoadingGetUnassigned: boolean) => {
		const leadName = inbox[currentLeadIndex]?.prospect?.name || "this lead";
		const conversations = inbox[currentLeadIndex]?.prospect?.conversations
			?.filter((c) => !!c)
			?.sort((a, b) => new Date(a.sentAt).getTime() - new Date(b.sentAt).getTime());
		// Full "Tell AI" bar for an empty chat; otherwise (conversations and/or drafts,
		// PREC-3697 — or the collapsed manual-write entry) the entry point stays reachable
		// as a slim strip pinned above the draft editor.
		const { showInlineBar, showCollapsedStrip } = resolveGenerateEntryVisibility({
			hasConversations: (conversations?.length ?? 0) > 0,
			drafts,
			isCopywritingActive: !!isCopywritingActive,
			collapsedGenerateBar,
			hideGenerateBar,
		});

		// When the first draft is a LinkedIn invite, follow-ups are only sent once
		// the connection request is accepted. Render their timing relative to the
		// invite ("N days later") instead of an absolute date; the real send date
		// depends on the acceptance moment. Null when the first draft is not an invite.
		const inviteBaselineDays = drafts[0]?.isInviteNote
			? (drafts[0]?.timing?.scheduledInDays ?? 0)
			: null;
		const activeIsInviteFollowUp = inviteBaselineDays !== null && activeDraftIndex > 0;

		return (
			<ChatlogFrame
				blurred={selected.length > 0}
				lead={inbox[currentLeadIndex]}
				panelLayout={panelLayout}
				scrollRef={chatlogScrollRef}
				showColumn={!isLoadingGetUnassigned && inbox.length > 0}
				footer={
					<>
						{showCollapsedStrip && (
							<div className="px-4">
								<GenerateBarCollapsedStrip
									className="max-w-full"
									failure={copywritingFailureOf(inbox[currentLeadIndex])}
									onOpen={() => openGeneratePanel()}
								/>
							</div>
						)}
						{fallbackNotice}
						<DraftEditorPanel
							activeDraftIndex={activeDraftIndex}
							activeIsInviteFollowUp={activeIsInviteFollowUp}
							canSaveCopywritingDraft={canSaveCopywritingDraft}
							composerRef={composerRef}
							drafts={drafts}
							inviteBaselineDays={inviteBaselineDays}
							isCollapsed={isEditorCollapsed}
							isCopywritingActive={!!isCopywritingActive}
							isSavingCopywritingDraft={isSavingCopywritingDraft}
							lead={inbox[currentLeadIndex]}
							message={message}
							portalContainer={portalContainer}
							recipientOptions={recipientOptions}
							renderTemplateButton={renderTemplateButton}
							sendingUsers={sendingUsers?.data ?? []}
							senderOptions={senderOptions}
							signatureDefault={draftSignature.signatureDefault}
							suggestions={suggestions}
							variables={variables}
							onAddFollowUp={handleAddFollowUp}
							onBccChange={handleUpdateDraftBcc}
							onCcChange={handleUpdateDraftCc}
							onExpand={() => setIsEditorCollapsed(false)}
							pendingInvitation={pendingInvitation}
							onFromChange={(option) => handleUpdateDraftFrom(activeDraftIndex, option)}
							onIncludeSignatureChange={draftSignature.setActiveIncludeSignature}
							onInsertVariable={insertVariableIntoActiveDraft}
							onMessageChange={setMessage}
							onOpenGeneratePanel={() => openGeneratePanel()}
							onOpenSendTest={() => setShowSendTestModal(true)}
							onSaveCopywritingDraft={() => saveCopywritingDraft()}
							onSend={() => handleSendMessage()}
							onSendFromColleague={(option) => handleUpdateDraftFrom(activeDraftIndex, option)}
							onSubjectChange={handleUpdateDraftSubject}
							onTimingChange={(timing) => handleUpdateDraftTiming(activeDraftIndex, timing)}
							onToChange={(option) => handleUpdateDraftTo(activeDraftIndex, option)}
							onToggleCcBcc={handleToggleCcBcc}
							onToggleCollapsed={() => setIsEditorCollapsed((v) => !v)}
						/>
					</>
				}
				thread={
					<>
						{showInlineBar && (
							<div
								className={classNames(
									"flex w-full",
									panelLayout ? "" : "flex-1 items-center justify-center"
								)}
							>
								<div className={panelLayout ? "w-full" : "w-[560px] max-w-full"}>
									{renderGenerateBar({
										hasConversations: false,
										onGenerationComplete: injectAiDrafts,
										triggerDescription: inbox[currentLeadIndex]?.trackerSignalResult?.summary,
									})}
								</div>
							</div>
						)}
						<ThreadConversationList
							conversations={conversations}
							lastReceivedRef={lastReceivedRef}
							lead={inbox[currentLeadIndex]}
							portalContainer={portalContainer}
							onSaveAsTemplate={handleSaveAsTemplate}
						/>
						{/* Draft cards */}
						{(drafts.length > 1 &&
							drafts.map((draft, index) => (
								<div
									key={draft.id}
									className="w-full"
									data-draft-card
									{...(index === 0 ? { "data-tour": "draft-reply-card" } : {})}
								>
									<DraftCard
										draft={draft}
										hasChannelMismatch={hasDraftChannelMismatch(draft)}
										index={index}
										inviteBaselineDays={
											inviteBaselineDays !== null && index > 0 ? inviteBaselineDays : undefined
										}
										isActive={index === activeDraftIndex}
										isCollapsed={!!collapsedDrafts[draft.id]}
										lead={inbox[currentLeadIndex]}
										leadName={leadName}
										portalContainer={portalContainer}
										precedingIsInviteNote={!!drafts[index - 1]?.isInviteNote}
										senderContext={resolveSenderContext(draft.from, sendingUsers?.data ?? [])}
										signatureDefault={draftSignature.signatureDefault}
										sendFromOptions={senderOptions}
										sendToOptions={recipientOptions}
										suggestions={suggestions}
										totalDrafts={drafts.length}
										unsubscribeLabel={t("inbox.variable.unsubscribeChip")}
										waitsForAcceptance={waitsForAcceptance(draft)}
										onClick={() => {
											setActiveDraftIndex(index);
											setIsEditorCollapsed(false);
										}}
										onDelete={() => handleDeleteDraft(index)}
										onFromChange={(option) => handleUpdateDraftFrom(index, option)}
										onTimingChange={(timing) => handleUpdateDraftTiming(index, timing)}
										onToChange={(option) => handleUpdateDraftTo(index, option)}
										onToggleCollapse={() => handleToggleCollapse(draft.id)}
									/>
								</div>
							))) ||
							""}
					</>
				}
			>
				{showTemplateModal && renderMessageTemplatesModal()}
				{showCreateTemplateModal && renderCreateTemplateModal()}

				{showSendTestModal && drafts[activeDraftIndex] && (
					<SendTestChatMessageModal
						defaultRecipientEmail={user?.email}
						draft={drafts[activeDraftIndex]}
						isOpen={showSendTestModal}
						listPersonId={inbox[currentLeadIndex]?.id}
						onClose={() => setShowSendTestModal(false)}
					/>
				)}

				{undoable.pendingSend && (
					<UndoSendToast
						acceptanceDelayDays={undoable.pendingSend.acceptanceDelayDays}
						isUndoing={undoable.isUndoing}
						messageCount={undoable.pendingSend.messageCount}
						portalContainer={portalContainer}
						onUndo={undoable.undo}
					/>
				)}

				{renderGeneratePanel()}
				{overrideModal}
				{senderConfirmModal}
				{renderMessageSnackbar({
					vertical: "top",
					horizontal: "center",
					onClick: () => {
						navigate(`/app/messages/outbox`);
					},
				})}
			</ChatlogFrame>
		);
	};

	// Floating Generate Messages panel (portal); also rendered by feed-only tabs.
	const renderGeneratePanel = () =>
		isCopywritingActive &&
		showGeneratePanel && (
			<FloatingGeneratePanel
				portalContainer={portalContainer}
				scopeToContainer={scopeGeneratePanelToPortal}
				onRequestClose={handleRequestCloseGeneratePanel}
			>
				{renderGenerateBar({
					hasConversations: (inbox[currentLeadIndex]?.prospect?.conversations?.length ?? 0) > 0,
					onGenerationComplete: handleGenerationComplete,
					triggerDescription:
						generateTriggerDescriptionOverride ??
						inbox[currentLeadIndex]?.trackerSignalResult?.summary,
				})}
			</FloatingGeneratePanel>
		);

	const renderSendButtons = () => (
		<ChatSendBar
			draftCount={drafts.length}
			isSendDisabled={isBlocked || !!undoable.pendingSend || isSendRunning}
			isSending={isSendingMessage || undoable.isSending}
			sendableCount={drafts.filter(isSendableDraft).length}
			waitsForAcceptance={drafts.filter(isSendableDraft).some(waitsForAcceptance)}
			onClearAll={handleDeleteAllDrafts}
			onSend={() => handleSendMessage()}
		/>
	);

	return {
		renderChatlog,
		isSendingMessage,
		message,
		setMessage,
		handleSendMessage,
		renderSendButtons,
		handleOpenTemplateModal,
		renderTemplateButton,
		renderMessageTemplatesModal,
		showTemplateModal,
		dropdownPosition,
		setDropdownPosition,
		scrollToBottom,
		// New draft-related exports
		drafts,
		activeDraftIndex,
		setActiveDraftIndex,
		handleAddFollowUp,
		handleDeleteDraft,
		handleToggleCollapse,
		collapsedDrafts,
		injectAiDrafts,
		openGeneratePanel,
		renderGeneratePanel,
		senderOptions,
	};
};
